import { useCallback, useEffect, useMemo } from "react";
import type { JSX } from "react";
import { useAnalytics } from "../../analytics/provider";
import { Icon, ModuleListItem } from "../../components/ui";
import {
  ModuleLayout,
  useListSelection,
  useSearchFilter,
} from "../../layout/ModuleLayout";
import { ListPane } from "../../components/layout/ListPane";
import { DetailPane } from "../../components/layout/DetailPane";
import { AccountsPanel } from "./AccountsPanel";
import { IdentityPanel } from "./IdentityPanel";
import { GroupsPanel } from "./GroupsPanel";
import { AgentPanel } from "./AgentPanel";
import { ProvidersPanel } from "./ProvidersPanel";
import { ModelsPanel } from "./ModelsPanel";
import { BillingPanel } from "./BillingPanel";
import { SearchIndexingPanel } from "./SearchIndexingPanel";
import { ModuleSettingsPanel } from "./ModuleSettingsPanel";
import { AppearancePanel } from "./AppearancePanel";
import { SourcesPanel } from "./SourcesPanel";
import { CATEGORY_GROUPS } from ".";
import { useSettingsStore } from "./store";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";
import { useRouterContext } from "../../providers/RouterProvider";

const DETAIL_PANELS: Record<string, JSX.Element> = {
  accounts: <AccountsPanel />,
  sources: <SourcesPanel />,
  identity: <IdentityPanel />,
  groups: <GroupsPanel />,
  agent: <AgentPanel />,
  providers: <ProvidersPanel />,
  models: <ModelsPanel />,
  billing: <BillingPanel />,
  search: <SearchIndexingPanel />,
  modules: <ModuleSettingsPanel />,
  appearance: <AppearancePanel />,
};

export function SettingsModule(): JSX.Element {
  const analytics = useAnalytics();
  const isHostedDemo = analytics.runtimeSurface === "hosted_demo";
  // @tested-by: tst_fe_hosted_demo_004
  // @invariant: operator-owned indexing settings are not routable in Hosted Demo.
  const categoryGroups = useMemo(
    () =>
      isHostedDemo
        ? CATEGORY_GROUPS.map((group) =>
            group.filter((category) => category.id !== "search"),
          ).filter((group) => group.length > 0)
        : CATEGORY_GROUPS,
    [isHostedDemo],
  );
  const categories = useMemo(() => categoryGroups.flat(), [categoryGroups]);
  const storedSelectedId = useSettingsStore((s) => s.selectedCategoryId);
  const searchQuery = useSettingsStore((s) => s.searchQuery);
  const { setSelectedCategoryId: setStoredSelectedId, setSearchQuery } = useSettingsStore((s) => s.actions);
  const router = useRouterContext();

  const routeSelectedId =
    router.activeModuleId === "settings" &&
    categories.some((category) => category.id === router.entityType)
      ? router.entityType
      : undefined;
  const storedVisibleSelectedId = categories.some(
    (category) => category.id === storedSelectedId,
  )
    ? storedSelectedId
    : undefined;
  const selectedId = routeSelectedId ?? storedVisibleSelectedId;

  const setSelectedId = useCallback((id: string | undefined) => {
    setStoredSelectedId(id);
    router.navigate("settings", id);
  }, [router, setStoredSelectedId]);

  useListSelection(categories, selectedId, setSelectedId);

  useEffect(() => {
    if (routeSelectedId && storedSelectedId !== routeSelectedId) {
      setStoredSelectedId(routeSelectedId);
    }
  }, [routeSelectedId, storedSelectedId, setStoredSelectedId]);

  useEffect(() => {
    if (router.activeModuleId !== "settings" || routeSelectedId) {
      return;
    }
    const fallbackId = storedVisibleSelectedId ?? categories[0]?.id;
    if (fallbackId) {
      router.navigate("settings", fallbackId);
    }
  }, [categories, router, routeSelectedId, storedVisibleSelectedId]);

  const filtered = useSearchFilter(categories, searchQuery, (c, q) =>
    c.title.toLowerCase().includes(q) || c.id.toLowerCase().includes(q),
  );

  const filteredGroups = useMemo(() => {
    const filteredIds = new Set(filtered.map((c) => c.id));
    return categoryGroups
      .map((group) => group.filter((c) => filteredIds.has(c.id)))
      .filter((group) => group.length > 0);
  }, [categoryGroups, filtered]);

  const cat = selectedId
    ? categories.find((c) => c.id === selectedId)
    : undefined;

  const runtime = useAppRuntime();
  useEffect(() => {
    if (!selectedId) {
      runtime.agent.setActiveContext(null);
      return;
    }
    runtime.agent.setActiveContext({
      moduleId: "settings",
      entityId: selectedId,
      entityTitle: cat?.title,
    });
  }, [selectedId, cat?.title, runtime]);

  return (
    <ModuleLayout
      moduleName="Settings"
      listPane={
        <ListPane
          title="Settings"
          onSearch={setSearchQuery}
        >
          {filteredGroups.map((group, gi) => (
            <div key={group[0]?.id ?? gi}>
              {gi > 0 && (
                <div className="my-1 mx-6 border-t border-edge" />
              )}
              {group.map((c) => {
                const isSelected = c.id === selectedId;
                return (
                  <div
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => { setSelectedId(c.id); }}
                  >
                    <ModuleListItem selected={isSelected}>
                      <Icon
                        name={c.icon}
                        size={18}
                        className={isSelected ? "text-content" : "text-content-secondary"}
                      />
                      <span
                        className={`text-sm ${
                          isSelected
                            ? "text-content font-medium"
                            : "text-content-secondary"
                        } ${!c.enabled ? "opacity-40" : ""}`}
                      >
                        {c.title}
                      </span>
                    </ModuleListItem>
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-10 px-4 text-center text-content-tertiary">
              No results found
            </div>
          )}
        </ListPane>
      }
      detailPane={
        selectedId && DETAIL_PANELS[selectedId] ? (
          <DetailPane>{DETAIL_PANELS[selectedId]}</DetailPane>
        ) : (
          <DetailPane>
            <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
              {cat?.title ?? "This section"} settings coming soon.
            </div>
          </DetailPane>
        )
      }
    />
  );
}
